import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import useAuth from '@contexts/useAuth';
import { billingGetConversationUsage } from '@api/billing';
import type { ProcessRunUsageSummary, UsageDetailResponse } from '@api/billing';
import MessageRequestGroup from './MessageRequestGroup';
import {
  GroupContainer,
  GroupHeader,
  HeaderLeft,
  DateRange,
  UserName,
  HeaderRight,
  CreditsBadge,
  MessageCount,
  SourceBadge,
  ExpandIcon,
  DetailContainer,
  DetailLoading,
  LegacyTable,
  LegacyRow,
  LegacyDate,
  LegacyAmount,
  RetryLink,
} from './ConversationGroup.styled';
import {
  TitleRow,
  ProcessName,
  RunNumber,
  TestRunChip,
  SubtitleRow,
  StatusPill,
} from './ProcessRunGroup.styled';

function formatDateRange(first: string, last: string): string {
  const firstDate = new Date(first);
  const lastDate = new Date(last);
  if (firstDate.toDateString() === lastDate.toDateString()) {
    const dateStr = firstDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const timeOpts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
    return `${dateStr}, ${firstDate.toLocaleTimeString(undefined, timeOpts)} — ${lastDate.toLocaleTimeString(undefined, timeOpts)}`;
  }
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  return `${firstDate.toLocaleString(undefined, opts)} — ${lastDate.toLocaleString(undefined, opts)}`;
}

function formatLegacyDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Maps the backend's RunStatus enum name to a visual tone for the status pill.
 * Unknown values default to neutral so we never crash if a new status is added.
 */
function statusToTone(status: string | null): 'neutral' | 'success' | 'warning' | 'danger' {
  if (!status) return 'neutral';
  switch (status.toUpperCase()) {
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
      return 'danger';
    case 'CANCELLED':
      return 'warning';
    case 'RUNNING':
    case 'WAITING_FOR_ACTION':
    case 'PENDING':
    default:
      return 'neutral';
  }
}

interface Props {
  summary: ProcessRunUsageSummary;
}

const ProcessRunGroup: React.FC<Props> = ({ summary }) => {
  const intl = useIntl();
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);

  const toggle = () => setExpanded((prev) => !prev);

  const detailQuery = useQuery<UsageDetailResponse>({
    queryKey: ['processRunUsage', user?.id, summary.runId],
    queryFn: () => billingGetConversationUsage(user!.token!, summary.runId),
    enabled: expanded && !!user?.token,
  });

  const legacyTransactions = detailQuery.data?.legacyTransactions ?? [];

  const processName = summary.processName
    ?? intl.formatMessage({ id: 'billing.processRun.untitled', defaultMessage: 'Untitled process' });

  return (
    <GroupContainer data-testid="process-run-group">
      <GroupHeader onClick={toggle} type="button" aria-expanded={expanded}>
        <HeaderLeft>
          <TitleRow>
            <ProcessName>{processName}</ProcessName>
            {summary.runNumber != null && (
              <RunNumber>
                {intl.formatMessage(
                  { id: 'billing.processRun.runNumber', defaultMessage: 'Run #{number}' },
                  { number: summary.runNumber },
                )}
              </RunNumber>
            )}
            {summary.isTest && (
              <TestRunChip>
                {intl.formatMessage({ id: 'billing.processRun.testRun', defaultMessage: 'Test run' })}
              </TestRunChip>
            )}
          </TitleRow>
          <SubtitleRow>
            <DateRange>{formatDateRange(summary.firstActivityAt, summary.lastActivityAt)}</DateRange>
            {summary.userName && <UserName>{summary.userName}</UserName>}
          </SubtitleRow>
        </HeaderLeft>
        <HeaderRight>
          <MessageCount>
            {intl.formatMessage(
              { id: 'billing.usage.nodes', defaultMessage: '{count, plural, one {# node} other {# nodes}}' },
              { count: summary.nodeCount },
            )}
          </MessageCount>
          <CreditsBadge>
            {summary.totalCredits.toLocaleString(undefined, { maximumFractionDigits: 4 })}
            {' '}
            {intl.formatMessage({ id: 'billing.usage.credits', defaultMessage: 'credits' })}
          </CreditsBadge>
          {summary.status && (
            <StatusPill $tone={statusToTone(summary.status)} data-testid="process-run-status">
              {summary.status.toLowerCase().replace(/_/g, ' ')}
            </StatusPill>
          )}
          <SourceBadge>
            {intl.formatMessage({ id: 'billing.sourceApp.osProcess', defaultMessage: 'Aibuild OS — Processes' })}
          </SourceBadge>
          <ExpandIcon $expanded={expanded}>▼</ExpandIcon>
        </HeaderRight>
      </GroupHeader>

      {expanded && (
        <DetailContainer>
          {detailQuery.isLoading && (
            <DetailLoading>
              {intl.formatMessage({ id: 'billing.usage.loadingDetail', defaultMessage: 'Loading details...' })}
            </DetailLoading>
          )}
          {detailQuery.isError && (
            <DetailLoading>
              {intl.formatMessage({ id: 'billing.usage.loadFailed', defaultMessage: 'Failed to load details.' })}
              {' '}
              <RetryLink type="button" onClick={() => detailQuery.refetch()}>
                {intl.formatMessage({ id: 'billing.usage.retry', defaultMessage: 'Retry' })}
              </RetryLink>
            </DetailLoading>
          )}
          {detailQuery.data?.messageRequests.map((req) => (
            <MessageRequestGroup key={req.messageRequestId} detail={req} />
          ))}
          {legacyTransactions.length > 0 && (
            <LegacyTable data-testid="legacy-usage-table">
              {legacyTransactions.map((tx) => (
                <LegacyRow key={tx.id}>
                  <LegacyDate>{formatLegacyDate(tx.createdAt)}</LegacyDate>
                  <LegacyAmount>
                    {tx.amount.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                    {' '}
                    {intl.formatMessage({ id: 'billing.usage.credits', defaultMessage: 'credits' })}
                  </LegacyAmount>
                </LegacyRow>
              ))}
            </LegacyTable>
          )}
        </DetailContainer>
      )}
    </GroupContainer>
  );
};

export default ProcessRunGroup;
